import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import translate from '../../../commonComponents/translate';
import {
  findCurrencySymbol,
  formatPrice,
  PLAN_STATUS,
} from '../../../services/utilityFunctions';
import CountryProp from '../../../commonComponents/forms/Address/CountryProp';
import StripeCardProp from '../../profile/propTypes/StripeCardProp';
import Notifications from '../../../services/notifications';
import ToggleSwitchWithLabel from '../../../commonComponents/forms/inputs/ToggleSwitchWithLabel';
import * as Utils from '../../../services/utilityFunctions';
import CurrencyProp from '../../profile/propTypes/currencyProp';
import PlanProp from '../propTypes/planProp';
import OrderHeaderItem from '../../shop/components/OrderHeaderItem';
import UpdatePaymentMethod from '../../shop/modal/UpdatePaymentMethod';
import SubscriptionPlanModal from '../../signIn/modals/SubscriptionPlanModal/SubscriptionPlanModal';
import ConfirmationPlanModal from '../../signIn/modals/SubscriptionPlanModal/ConfirmationPlanModal';
import ReactLoader from '../../../commonComponents/loader/ReactLoader';
import Request from '../../../services/request';
import ConfigsContext from '../../../contextProviders/ConfigsContext';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';

function PlanHeader({
  t,
  countries,
  plan,
  paramCards,
  currencies,
  _csrf,
  tenant,
}) {
  const {
    status,
    startDate,
    paymentMethodId,
    stripeSubscription,
    currentUserPlan,
  } = plan;
  const [modalUpdatePayment, setModalUpdatePayment] = useState({ show: false });
  const [modalChangePlan, setModalChangePlan] = useState({ show: false });
  const [modalConfirmation, setModalConfirmation] = useState({
    show: false,
    selectedPlan: {},
  });
  const [paymentMethodIdValue, setPaymentMethodId] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const showModalUpdatePayment = () => {
    setModalUpdatePayment({ show: true });
  };
  const showModalChangePlan = () => setModalChangePlan({ show: true });
  const [hasPlans, setHasPlans] = useState(true);

  const browserHistory = useHistory();

  const currentCard = paramCards.slice()?.find(i => i.id === paymentMethodId);
  const cardNumberLast4 =
    (currentCard && Utils.displayCardNumber(currentCard.last4)) || null;
  const { currentPeriodEnd = '' } = stripeSubscription || {};

  const planWithFaults =
    status === PLAN_STATUS.PAST_DUE ||
    status === PLAN_STATUS.UNPAID ||
    status === PLAN_STATUS.FAILED ||
    status === PLAN_STATUS.FAILED_AND_EXPIRED ||
    status === PLAN_STATUS.CANCELED;

  useEffect(() => {
    if (!hasPlans) {
      window.location.href = '/';
    }
  }, [hasPlans]);

  const cancelPlan = useCallback(async () => {
    setIsLoading(true);
    axios({
      url: `/api/subscription/${plan._id}/cancel`,
      method: 'PUT',
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'License',
        'X-CSRF-Token': _csrf,
        _csrf,
      },
    })
      .then(() => {
        setIsLoading(false);
        Notifications.showNotificationSuccess(
          t('success'),
          t('plan.cancel.success'),
        );
        window.location.href = '/dashboard';
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'plan.cancel.error');
        console.log(err);
        setIsLoading(false);
      });
  }, [plan._id, setIsLoading]);

  const updateMethodPayment = useCallback(async () => {
    setIsLoading(true);
    axios({
      url: '/api/subscription/subscribe',
      method: 'POST',
      data: {
        currentUserPlan: currentUserPlan._id,
        paymentMethodId: paymentMethodIdValue,
      },
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'License',
        'X-CSRF-Token': _csrf,
        _csrf,
      },
    })
      .then(() => {
        setIsLoading(false);
        Notifications.showNotificationSuccess(
          t('success'),
          t('plan.paymentMethod.success'),
        );
        setModalUpdatePayment({ show: false });
        browserHistory.replace(`${window.location.pathname}?open=true`);
        browserHistory.go(0);
      })
      .catch(err => {
        setIsLoading(false);
        Notifications.showErrorFromRequest(err, 'plan.paymentMethod.error');
        console.log(err);
        setIsLoading(false);
      });
  }, [paymentMethodIdValue, currentUserPlan._id, setIsLoading]);

  const image = currentUserPlan.imageSquare;
  const imageURL =
    image && image?.thumb ? image?.thumb : '/images/no_image_upload.png';

  const isPlanActive =
    status === PLAN_STATUS.ACTIVE || status === PLAN_STATUS.TRIAL;

  // BRAVON-6254 — Pay-with-Wallet user-level toggle.
  // Mirrors web_admin_portal/react/sections/settings/configurations/features
  //   /WalletPayFeature.jsx → getEffectivePaymentProvider:
  // per-business-country providers take precedence; stripe outranks
  // stripe_invoices; falls back to the tenant default. The pay-with-wallet
  // feature is only valid when the effective provider is `stripe_invoices`.
  const { organization } = useContext(ConfigsContext);
  const { loggedUser } = useContext(LoggedUserContext);
  const orgPayWithWalletEnabled =
    organization?.services?.payWithWallet?.enabled === true;
  const effectivePaymentProvider = useMemo(() => {
    if (!tenant || !tenant.settings) return 'none';
    const businessProviders = (
      tenant.settings.paymentProviderBusinessCountries || []
    )
      .filter(
        b =>
          !b.organization ||
          String(b.organization) === String(organization?._id || ''),
      )
      .map(b => b.paymentProviderType)
      .filter(p => p && p !== 'none');
    if (businessProviders.indexOf('stripe') !== -1) return 'stripe';
    if (businessProviders.indexOf('stripe_invoices') !== -1) {
      return 'stripe_invoices';
    }
    if (businessProviders.indexOf('bank_transfer_manual') !== -1) {
      return 'bank_transfer_manual';
    }
    return tenant.settings.paymentProviderType || 'none';
  }, [tenant, organization?._id]);
  const tenantHasStripeInvoices =
    effectivePaymentProvider === 'stripe_invoices';
  const showPayWithWalletToggle =
    isPlanActive && orgPayWithWalletEnabled && tenantHasStripeInvoices;
  const [payWithWalletForSubscription, setPayWithWalletForSubscription] =
    useState(Boolean(loggedUser?.walletSettings?.payWithWalletForSubscription));
  const [isSavingPayWithWallet, setIsSavingPayWithWallet] = useState(false);

  const handlePayWithWalletToggle = useCallback(
    value => {
      if (isSavingPayWithWallet) return;
      const next = Boolean(value);
      const previous = payWithWalletForSubscription;
      setPayWithWalletForSubscription(next);
      setIsSavingPayWithWallet(true);
      new Request(_csrf)
        .put('/profile/edit', {
          walletSettings: {
            ...(loggedUser?.walletSettings || {}),
            payWithWalletForSubscription: next,
          },
        })
        .done(() => {
          setIsSavingPayWithWallet(false);
          Notifications.showNotificationSuccess(
            t('success'),
            t('plan.payWithWallet.success'),
          );
        })
        .fail(err => {
          setIsSavingPayWithWallet(false);
          setPayWithWalletForSubscription(previous);
          Notifications.showErrorFromRequest(err, 'plan.payWithWallet.error');
        });
    },
    [
      _csrf,
      isSavingPayWithWallet,
      loggedUser,
      payWithWalletForSubscription,
      t,
    ],
  );

  return (
    <>
      {modalConfirmation.show && modalConfirmation.selectedPlan && (
        <ConfirmationPlanModal
          t={t}
          plan={modalConfirmation.selectedPlan}
          onClose={() => {
            setModalConfirmation({ show: false, selectedPlan: {} });
            browserHistory.replace(`${window.location.pathname}?open=true`);
            browserHistory.go(0);
          }}
        />
      )}
      {modalUpdatePayment.show && (
        <UpdatePaymentMethod
          tenant={tenant}
          subscription={plan}
          t={t}
          submitPaymentMethod={updateMethodPayment}
          onClose={() => setModalUpdatePayment({ show: false })}
          _csrf={_csrf}
          setPaymentMethodId={setPaymentMethodId}
          isFromPlan
        />
      )}
      {modalChangePlan.show && (
        <SubscriptionPlanModal
          t={t}
          _csrf={_csrf}
          tenant={tenant}
          fromMyPlan
          countries={countries}
          setHasPlans={setHasPlans}
          onPlansModalClose={selectedPlan => {
            setModalChangePlan({ show: false });
            setModalConfirmation({ show: true, selectedPlan });
          }}
          onClose={() => {
            setModalChangePlan({ show: false });
          }}
          showClose
        />
      )}
      <ReactLoader isLoading={isLoading}>
        <div className="plan-header-wrapper">
          <div className="user-thumbnail">
            <div className="user-thumbnail-item">
              <img src={imageURL} alt={currentUserPlan.name} />
            </div>
          </div>
          <div className="item flex-end initial-info">
            <div className="plan-info">
              <div className="user-name">
                <span>{currentUserPlan.name}</span>
              </div>
              <div className="pricing-value">
                <span className="price-notvat">
                  {`${formatPrice(
                    currentUserPlan.total,
                    findCurrencySymbol(
                      currentUserPlan.userPrice?.code,
                      currencies,
                    ) || '€',
                  )}/ ${t(`subscription.${currentUserPlan.interval}`)}`}
                </span>
                <span className="price-withvat">
                  {`${formatPrice(
                    currentUserPlan.subtotal,
                    findCurrencySymbol(
                      currentUserPlan.userPrice?.code,
                      currencies,
                    ) || '€',
                  )} ${t('vat.excluded.abbreviation')}`}
                </span>
              </div>
            </div>
            {status && (
              <div className="order-item-wrapper">
                <span className="label-item">
                  {t('order.subscription.status')}
                </span>
                {isPlanActive && (
                  <span className="value-item">
                    <img
                      src="/images/plan/active-icon.svg"
                      alt="active"
                      className="active-checkbox"
                    />
                    {t('order.active')}
                  </span>
                )}
                {planWithFaults && (
                  <span className="value-item">
                    <img
                      src="/images/plan/failed-icon.svg"
                      alt="active"
                      className="plan-checkbox"
                    />
                    {t('plan.payment_failed')}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="item flex-end ">
            {startDate && (
              // eslint-disable-next-line max-len
              <OrderHeaderItem
                label={t('plan.startDate')}
                value={moment(startDate).format('DD MMMM YYYY')}
              />
            )}
          </div>
          <div className="item flex-end">
            {stripeSubscription?.currentPeriodEnd && (
              // eslint-disable-next-line max-len
              <OrderHeaderItem
                label={t('plan.nextPayment')}
                value={moment(currentPeriodEnd * 1000).format('DD MMMM YYYY')}
              />
            )}
          </div>
          <div className="item flex-end">
            <OrderHeaderItem
              label={t('sessionDetails_modal_coupon')}
              value={plan?.currentUserPlan?.coupon?.code || '-'}
            />
          </div>
          {cardNumberLast4 && (
            <div className="item flex-end">
              <div className="order-item-wrapper">
                <span className="label-item">
                  {t('order_detail_right.paymentMethod')}
                  {(isPlanActive ||
                    status === PLAN_STATUS.FAILED ||
                    status === PLAN_STATUS.FAILED_AND_EXPIRED) && (
                    <button
                      type="button"
                      onClick={showModalUpdatePayment}
                      className="item-edit"
                    >
                      <i className="fa fa-pen" />
                    </button>
                  )}
                </span>
                <span className="value-item">
                  <span className="m-card_item__head">
                    <img
                      src="/images/shop/card.svg"
                      alt="Credit Card preview"
                      className="card-preview-img"
                    />
                    <span className="m-card_item__title">
                      {cardNumberLast4}
                    </span>
                  </span>
                </span>
              </div>
            </div>
          )}
          <div className="plan-actions">
            {(isPlanActive ||
              status === PLAN_STATUS.FAILED ||
              status === PLAN_STATUS.FAILED_AND_EXPIRED) && (
              <div className="cancel-button">
                <button type="button" className="btn" onClick={cancelPlan}>
                  {t('plan.cancel')}
                </button>
              </div>
            )}
            {isPlanActive && (
              <div className="change-plan-button">
                <button
                  type="button"
                  className="btn btn-dynamic"
                  onClick={showModalChangePlan}
                >
                  {t('plan.changePlan')}
                </button>
              </div>
            )}
            {planWithFaults && (
              <div className="reactivate-button">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={showModalChangePlan}
                >
                  {t('plan.reactivate')}
                </button>
              </div>
            )}
          </div>
        </div>
        {showPayWithWalletToggle && (
          <div className="plan-header-row plan-header-row--payWithWallet">
            <ToggleSwitchWithLabel
              id="payWithWalletForSubscription"
              name="payWithWalletForSubscription"
              label={t('plan.payWithWallet.label')}
              value={payWithWalletForSubscription}
              onChange={handlePayWithWalletToggle}
              disabled={isSavingPayWithWallet}
              hint={t('plan.payWithWallet.hint')}
              wrapperClass="form-group m-form__group row"
            />
          </div>
        )}
      </ReactLoader>
    </>
  );
}

PlanHeader.propTypes = {
  t: PropTypes.func.isRequired,
  plan: PropTypes.shape(PlanProp.propType).isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  paramCards: PropTypes.arrayOf(PropTypes.shape(StripeCardProp.propType))
    .isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  _csrf: PropTypes.string.isRequired,
  tenant: PropTypes.shape({}).isRequired,
};

export default translate(PlanHeader);
